import {
  CalendarClock,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CornerUpLeft,
  MapPin,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ConversationBooking } from '../../../api/types';
import { formatDateTime } from '../../../lib/dateFormat';
import { useTimeFormat } from '../../../lib/tenantTime';
import { type BookingKind, bookingLabel } from './dispatchLink';
import { JobLink } from './JobLink';
import { clientRef, ourRef } from '../../../lib/reservationRef';

/**
 * What this conversation has already produced in dispatch, at the top of the
 * thread.
 *
 * The question it answers is the one an agent opening a thread cannot answer
 * any other way: has this already been booked, and by whom, and from which
 * message. Before this the answer was a private note somewhere up the scroll —
 * findable if you knew to look, invisible if you did not, and silent about the
 * message it came from. Two agents working the same inbox is exactly when it
 * matters, which is exactly when nobody is scrolling.
 *
 * A RAIL, not a list. One thread routinely produces several bookings — a
 * forwarded reservation per message on a busy affiliate thread — and a list
 * would grow until it owned the screen, which is the one thing a header strip
 * may not do. So the strip is a fixed one-card height whatever the count, and
 * the cards are paged through: newest first, because the booking an agent is
 * being asked about is almost always the last one made.
 *
 * Paging is native scroll-snap rather than a transform track, which buys the
 * two gestures a hand-rolled carousel never gets right — trackpad swipe and
 * touch flick, with the platform's own momentum — and leaves the buttons as
 * ordinary `scrollBy` calls. The dots and the chevrons read the same scroll
 * position, so no two things can disagree about which card is showing.
 */

/** The kind decides the tone, and the tone is the first thing read. */
const KIND_CLASS: Record<BookingKind, string> = {
  draft: 'is-draft',
  quote: 'is-quote',
  job: 'is-job',
};

/**
 * The trip on one line: pickup → dropoff, or pickup alone for an hourly. Built
 * from the SNAPSHOT, which is what was booked from this thread — not a live
 * read of a row somebody may have edited since.
 */
function tripLine(booking: ConversationBooking): string {
  const { pickup, dropoff, stops } = booking.trip;
  if (!pickup && !dropoff) return booking.summary ?? '';
  const via = stops?.length ? ` · ${stops.length} stop${stops.length > 1 ? 's' : ''}` : '';
  if (pickup && dropoff) return `${pickup} → ${dropoff}${via}`;
  return `${pickup || dropoff}${via}`;
}

/**
 * Where each card sits, as a scroll offset.
 *
 * Measured rather than computed from `clientWidth`: the rail carries its own
 * horizontal padding and a gap between slides, so one page of scroll is a few
 * pixels SHORTER than the scrollport. Dividing by the scrollport width is
 * right for the first few cards and then drifts — by the fourteenth the
 * counter reads one card behind, which on a control whose whole job is saying
 * which card you are looking at is worse than no counter at all.
 */
function slideStops(rail: HTMLElement): number[] {
  const slides = [...rail.children] as HTMLElement[];
  if (!slides.length) return [];
  const base = slides[0].offsetLeft;
  return slides.map((slide) => slide.offsetLeft - base);
}

/** Which card the rail has come to rest on — the one nearest the left edge. */
function nearestSlide(rail: HTMLElement): number {
  const stops = slideStops(rail);
  let best = 0;
  let closest = Number.POSITIVE_INFINITY;
  stops.forEach((stop, index) => {
    const distance = Math.abs(stop - rail.scrollLeft);
    if (distance < closest) {
      closest = distance;
      best = index;
    }
  });
  return best;
}

function BookingCard({
  booking,
  onOpenMessage,
}: {
  booking: ConversationBooking;
  onOpenMessage?: (messageId: number) => void;
}) {
  const { zone, foreignZoneAbbr, formats } = useTimeFormat();
  const label = bookingLabel(booking.kind);
  // A pickup is a promise made to someone standing at a curb, so it renders in
  // the RIDE's zone — the same rule the contact panel's bookings card follows,
  // and the reason the snapshot carries a zone at all.
  const rideZone = booking.trip.timeZone ?? zone;
  const pickup = booking.pickupAt ? new Date(booking.pickupAt) : null;
  const when =
    pickup && !Number.isNaN(pickup.getTime()) ? formatDateTime(pickup, rideZone, formats) : null;
  const zoneBadge = pickup ? foreignZoneAbbr(booking.trip.timeZone, pickup) : null;
  const trip = tripLine(booking);
  const pax = booking.trip.passengerCount;

  return (
    <article className={`tbk-card ${KIND_CLASS[booking.kind]}`}>
      <div className="tbk-card-top">
        <span className="tbk-kind">{label}</span>
        {/* Inside the dashboard it opens the booking there (OPEN_JOB);
            elsewhere it links out. With no origin to link at, or an id the
            dashboard cannot address, the reference still reads and still copies
            — which is the whole of what an agent does with it when they pick
            up the phone. */}
        <JobLink kind={booking.kind} id={booking.dispatchId} className="tbk-ref" icon>
          {ourRef(booking.kind, booking.reference)}
        </JobLink>
        {booking.bookerReference && (
          <span className="tbk-their-ref" title="The sender's own reservation number">
            {clientRef(booking.bookerReference)}
          </span>
        )}
        {booking.messageId !== null && onOpenMessage && (
          <button
            type="button"
            className="tbk-jump"
            title="Show the message this was drafted from"
            onClick={() => onOpenMessage(booking.messageId as number)}
          >
            <CornerUpLeft size={11} />
            from this message
          </button>
        )}
      </div>
      <div className="tbk-card-line">
        {trip && (
          <span className="tbk-trip" title={trip}>
            <MapPin size={11} />
            {trip}
          </span>
        )}
        {when && (
          <span className="tbk-when">
            <CalendarClock size={11} />
            {when}
            {zoneBadge && <em className="tbk-zone">{zoneBadge}</em>}
          </span>
        )}
        {typeof pax === 'number' && (
          <span className="tbk-pax">
            <Users size={11} />
            {pax}
          </span>
        )}
      </div>
    </article>
  );
}

export function ThreadBookings({
  bookings,
  onOpenMessage,
}: {
  /** Newest first — the service orders them and this preserves it. */
  bookings: ConversationBooking[];
  onOpenMessage?: (messageId: number) => void;
}) {
  const railRef = useRef<HTMLDivElement | null>(null);
  // The page position is stored WITH the newest booking it was measured
  // against, so a booking arriving over the socket resets the counter in the
  // same render that adds the card rather than one render later — see the
  // effect below, which only has to move the rail.
  const [position, setPosition] = useState<{ newest: number | undefined; index: number }>({
    newest: undefined,
    index: 0,
  });
  // Per-viewer convenience and nothing more, so localStorage is the right home:
  // an agent who works a thread they have already booked wants the header back.
  const [collapsed, setCollapsed] = useState(
    () => globalThis.localStorage?.getItem('crm.threadBookings.collapsed') === '1',
  );
  const toggle = useCallback(() => {
    setCollapsed((was) => {
      globalThis.localStorage?.setItem('crm.threadBookings.collapsed', was ? '0' : '1');
      return !was;
    });
  }, []);

  /**
   * Which card is showing, read off the scroll position rather than tracked
   * beside it — a flick and a button press then land on the same number.
   *
   * The same-object bail-out is load-bearing rather than tidy: a momentum
   * scroll fires this once a frame, and returning a fresh object each time
   * would re-render the whole strip sixty times for a gesture that changes the
   * counter twice.
   */
  const newest = bookings[0]?.id;
  const onScroll = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    const index = nearestSlide(rail);
    setPosition((current) =>
      current.newest === newest && current.index === index ? current : { newest, index },
    );
  }, [newest]);

  // A booking arriving over the socket lands at the front, so the rail goes
  // back to it: the newest is what the strip is for, and one left parked on
  // card three hides the thing that just happened. Only the DOM is touched
  // here — the counter resets by reading `position.newest` below.
  useEffect(() => {
    railRef.current?.scrollTo({ left: 0, behavior: 'auto' });
  }, [newest]);

  const index = position.newest === newest ? position.index : 0;

  const page = (delta: number) => {
    const rail = railRef.current;
    if (!rail) return;
    const stops = slideStops(rail);
    const next = Math.max(0, Math.min(stops.length - 1, nearestSlide(rail) + delta));
    rail.scrollTo({ left: stops[next] ?? 0, behavior: 'smooth' });
  };

  if (!bookings.length) return null;
  const many = bookings.length > 1;

  return (
    <section
      className={`tbk${collapsed ? ' is-collapsed' : ''}`}
      aria-label="Reservations made from this conversation"
    >
      <header className="tbk-head">
        <button
          type="button"
          className="tbk-toggle"
          onClick={toggle}
          aria-expanded={!collapsed}
          title={collapsed ? 'Show the reservations from this thread' : 'Hide'}
        >
          <ChevronDown size={13} className="tbk-caret" />
          <span className="tbk-heading">
            {bookings.length === 1 ? 'Booked from this thread' : `${bookings.length} booked from this thread`}
          </span>
        </button>
        {many && !collapsed && (
          <div className="tbk-nav">
            <button
              type="button"
              className="tbk-arrow"
              onClick={() => page(-1)}
              disabled={index === 0}
              aria-label="Newer reservation"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="tbk-count" aria-live="polite">
              {index + 1}/{bookings.length}
            </span>
            <button
              type="button"
              className="tbk-arrow"
              onClick={() => page(1)}
              disabled={index === bookings.length - 1}
              aria-label="Older reservation"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </header>

      {!collapsed && (
        <div className="tbk-rail" ref={railRef} onScroll={onScroll}>
          {bookings.map((booking) => (
            <div className="tbk-slide" key={booking.id}>
              <BookingCard booking={booking} onOpenMessage={onOpenMessage} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
